import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The feed's toast (it can carry buttons: Undo, Not interested, Record) and
 * its glass sheet (Why this story, Fewer like this, Build this story?).
 * Brian's headlines.html v7: one sheet style, the toast above the tab bar.
 */

export interface ToastAction { label: string; run: () => void }
interface ToastState { id: number; body: ReactNode; actions: ToastAction[]; ms: number }

export function useFeedToast() {
  const [t, setT] = useState<ToastState | null>(null)
  const seq = useRef(0)
  const show = useCallback((body: ReactNode, ms = 2600, actions: ToastAction[] = []) => {
    seq.current += 1
    setT({ id: seq.current, body, actions, ms })
  }, [])
  const hide = useCallback(() => setT(null), [])
  useEffect(() => {
    if (!t) return
    const timer = setTimeout(() => setT((cur) => (cur && cur.id === t.id ? null : cur)), t.ms)
    return () => clearTimeout(timer)
  }, [t])
  const node = t ? createPortal(
    <div className={`gg feed-toast${t.actions.length ? ' feed-toast--act' : ''}`} role="status" key={t.id}>
      <span className="feed-toast__tx">{t.body}</span>
      {t.actions.map((a) => (
        <button key={a.label} type="button" className="feed-toast__ta" onClick={() => { setT(null); a.run() }}>{a.label}</button>
      ))}
    </div>,
    document.body,
  ) : null
  return { show, hide, node }
}

export function FeedSheet({ open, onClose, label, children }: {
  open: boolean
  onClose: () => void
  label: string
  children: ReactNode
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  return createPortal(
    <div className="gg feed-sheet-host">
      <div className={`feed-scrim${open ? ' is-on' : ''}`} onClick={onClose} />
      <div className={`feed-sheet${open ? ' is-on' : ''}`} role="dialog" aria-modal="true" aria-label={label} aria-hidden={!open}>
        {open && children}
      </div>
    </div>,
    document.body,
  )
}
